import styled from "styled-components";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
`;

/* ── Hero ── */
const Hero = styled.section`
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const HeroLead = styled.p`
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 640px;
`;

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
  justify-content: center;
`;

const Tab = styled.a`
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

/* ── Vision statement card ── */
const ContentSection = styled.section`
  padding: clamp(32px, 4vw, 48px) 0 clamp(80px, 10vw, 120px);
`;

const VisionCard = styled.div`
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  border-radius: 28px;
  padding: clamp(48px, 7vw, 88px) clamp(28px, 5vw, 56px);
  color: #ffffff;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-bottom: clamp(56px, 7vw, 88px);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 25% 20%,
      rgba(255, 255, 255, 0.18),
      transparent 60%
    );
    pointer-events: none;
  }

  p,
  h2 {
    position: relative;
  }
`;

const VisionEyebrow = styled.p`
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.82);
  margin: 0 0 18px;
`;

const VisionStatement = styled.h2`
  font-size: clamp(26px, 3.6vw, 40px);
  font-weight: 700;
  line-height: 1.22;
  letter-spacing: -0.02em;
  margin: 0 auto;
  max-width: 760px;

  em {
    font-style: normal;
    background: linear-gradient(180deg, transparent 62%, rgba(255, 255, 255, 0.22) 62%);
    padding: 0 4px;
  }
`;

/* ── Core values section ── */
const SectionHead = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const SectionEyebrow = styled.p`
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 12px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(28px, 3.6vw, 40px);
  font-weight: 700;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0;
`;

/* ── Value cards ── */
const ACCENTS = {
  indigo: {
    gradient: "linear-gradient(135deg, #eef2ff 0%, #ddd6fe 100%)",
    color: "#6366f1",
  },
  emerald: {
    gradient: "linear-gradient(135deg, #ecfdf5 0%, #a7f3d0 100%)",
    color: "#10b981",
  },
  amber: {
    gradient: "linear-gradient(135deg, #fffbeb 0%, #fed7aa 100%)",
    color: "#d97706",
  },
  purple: {
    gradient: "linear-gradient(135deg, #faf5ff 0%, #e9d5ff 100%)",
    color: "#9333ea",
  },
  rose: {
    gradient: "linear-gradient(135deg, #fff1f2 0%, #fecdd3 100%)",
    color: "#e11d48",
  },
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.article`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  overflow: hidden;
  transition: border-color 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: #c7d2fe;
    transform: translateY(-3px);
    box-shadow: 0 18px 35px -18px rgba(15, 23, 42, 0.15);
  }

  .visual {
    aspect-ratio: 16 / 9;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => ACCENTS[p.$accent].gradient};
    color: ${(p) => ACCENTS[p.$accent].color};
    position: relative;
    overflow: hidden;
  }

  .visual::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 30% 30%,
      rgba(255, 255, 255, 0.55),
      transparent 60%
    );
    pointer-events: none;
  }

  .visual svg {
    position: relative;
    z-index: 1;
  }

  .body {
    padding: 26px 26px 30px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 10px;
    letter-spacing: -0.01em;
  }

  p {
    font-size: 14.5px;
    line-height: 1.65;
    color: #64748b;
    margin: 0;
  }
`;

/* ── Icons ── */
const IconBase = ({ children }) => (
  <svg
    width="60"
    height="60"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {children}
  </svg>
);

const LightbulbIcon = () => (
  <IconBase>
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2V18h6v-1.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2Z" />
  </IconBase>
);

const AccessIcon = () => (
  <IconBase>
    <circle cx="12" cy="4" r="2" />
    <path d="M5 8h14" />
    <path d="M12 10v5" />
    <path d="M8 22l4-7 4 7" />
  </IconBase>
);

const HeartIcon = () => (
  <IconBase>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </IconBase>
);

const GlobeIcon = () => (
  <IconBase>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </IconBase>
);

const UserIcon = () => (
  <IconBase>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </IconBase>
);

const VALUES = [
  {
    key: "innovation",
    title: "Innovation",
    desc: "We use AI and data technology to deliver innovative solutions that help seniors live better lives.",
    accent: "indigo",
    Icon: LightbulbIcon,
  },
  {
    key: "accessibility",
    title: "Accessibility",
    desc: "User-friendly services anyone can use so the benefits of digital health reach every generation.",
    accent: "emerald",
    Icon: AccessIcon,
  },
  {
    key: "impact",
    title: "Social Impact",
    desc: "Supporting cognitive health reduces individual and societal burden, improving wellbeing in local and global communities.",
    accent: "rose",
    Icon: HeartIcon,
  },
  {
    key: "global",
    title: "Global Expansion",
    desc: "Setting a new standard for AI-driven health through collaboration and expansion across global markets.",
    accent: "purple",
    Icon: GlobeIcon,
  },
  {
    key: "personalization",
    title: "Personalization",
    desc: "Training programs tailored to each user's health data ensure the strongest possible outcomes.",
    accent: "amber",
    Icon: UserIcon,
  },
];

const TABS = [
  { key: "greetings", label: "Greetings", href: "/aboutUs/greetings" },
  { key: "vision", label: "Vision", href: "/aboutUs/vision" },
  { key: "history", label: "History", href: "/aboutUs/history" },
  { key: "map", label: "Map", href: "/aboutUs/map" },
];

export function Vision() {
  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Company</Eyebrow>
          <HeroTitle>Vision &amp; Core Values</HeroTitle>
          <HeroLead>
            What we believe and the principles that shape how we build digital
            health for an aging society.
          </HeroLead>
          <Tabs>
            {TABS.map((t) => (
              <Tab key={t.key} href={t.href} $active={t.key === "vision"}>
                {t.label}
              </Tab>
            ))}
          </Tabs>
        </Container>
      </Hero>

      <ContentSection>
        <Container>
          <VisionCard>
            <VisionEyebrow>Vision</VisionEyebrow>
            <VisionStatement>
              Through AI-based digital health innovation, we&apos;re solving
              the health challenges of an <em>aging society</em> and growing
              into a global leader in cognitive health support.
            </VisionStatement>
          </VisionCard>

          <SectionHead>
            <SectionEyebrow>Our values</SectionEyebrow>
            <SectionTitle>Five principles that guide us</SectionTitle>
          </SectionHead>

          <Grid>
            {VALUES.map((v) => (
              <ValueCard key={v.key} $accent={v.accent}>
                <div className="visual">
                  <v.Icon />
                </div>
                <div className="body">
                  <h3>{v.title}</h3>
                  <p>{v.desc}</p>
                </div>
              </ValueCard>
            ))}
          </Grid>
        </Container>
      </ContentSection>
    </Page>
  );
}
